/**
 * "E-mails que não viraram conta" (ALC-4471 / S5): the finance list of e-mails whose intake
 * outcome is `NO_BILL` (S1 contract), or that were retried and have no new outcome yet, their
 * files, and the retry action. Rows whose every file already has a live bill are not listed.
 *
 * API: `GET /email-intake/no-bill`, `GET /email-intake/no-bill/:messageId` (one row, fresh: the
 * "Enviar manualmente" pre-flight), `GET /email-intake/no-bill/:messageId/files/:fileId/download-url`,
 * `GET /email-intake/no-bill/:messageId/files/:fileId/content`,
 * `GET /email-intake/no-bill/:messageId/files.zip`, `POST /email-intake/no-bill/:messageId/retry`,
 * `POST /email-intake/no-bill/:messageId/dismiss`, `POST /email-intake/no-bill/:messageId/document`
 * ("Anexar documento"), `POST /email-intake/no-bill/:messageId/ask-sender` ("Pedir ao remetente").
 * All are gated by `ap:bill:create` and scoped to the caller's tenant and entities.
 * A dismissed e-mail leaves the list and stays stored, with who dismissed it and when.
 */

import type { EmailIntakeReasonCode } from './email-intake-outcome.js';
import type { StableRefusalCopy } from './stable-refusal.js';

/**
 * A live bill that already exists for a file (L3), as finance reads it: "Conta #N". When that bill
 * is in an entity outside the caller's entity scope, both fields are null: the file is covered
 * (do not bill it again) but the bill is not named.
 */
export interface EmailIntakeNoBillExistingBill {
  billId: string | null;
  /** N of "Conta #N": the bill's `vendorBillNumber`, else the first 8 characters of its id. */
  billNumber: string | null;
}

/** One file of a no-bill e-mail. `fileId` is opaque; pass it back to the file endpoints. */
export interface EmailIntakeNoBillFile {
  fileId: string;
  /** Sanitized base name (no folders, no control characters); also the download name. */
  filename: string;
  mimeType: string | null;
  sizeBytes: number | null;
  /** This file's own reason from the outcome details; null when not recorded (or retried). */
  reasonCode: EmailIntakeReasonCode | null;
  /** Live bill that already covers this file (same lookup as the worker's duplicate rule). */
  existingBill: EmailIntakeNoBillExistingBill | null;
}

/**
 * State of a listed row. Stable codes; resolve copy from `EMAIL_INTAKE_NO_BILL_ROW_STATE_COPY`.
 * - NO_BILL: the e-mail's recorded outcome is NO_BILL.
 * - RETRYING: "Tentar novamente" ran less than 2 h ago and the new run has no outcome yet.
 * - RETRY_NO_RESULT: the retry is older than 2 h and still has no outcome (L65 for retries).
 */
export const EMAIL_INTAKE_NO_BILL_ROW_STATES = {
  NO_BILL: 'EMAIL_INTAKE_NO_BILL_STATE_NO_BILL',
  RETRYING: 'EMAIL_INTAKE_NO_BILL_STATE_RETRYING',
  RETRY_NO_RESULT: 'EMAIL_INTAKE_NO_BILL_STATE_RETRY_NO_RESULT',
} as const;

export type EmailIntakeNoBillRowState =
  (typeof EMAIL_INTAKE_NO_BILL_ROW_STATES)[keyof typeof EMAIL_INTAKE_NO_BILL_ROW_STATES];

/** How long a retry counts as "Reprocessando" before it becomes "Reenvio sem resultado". */
export const EMAIL_INTAKE_NO_BILL_RETRY_STALE_AFTER_MS = 2 * 60 * 60 * 1000;

/** Copy per row state (pt-BR users, en web English locale). NO_BILL shows the reason instead. */
export const EMAIL_INTAKE_NO_BILL_ROW_STATE_COPY: Record<
  EmailIntakeNoBillRowState,
  { ptBr: string; en: string }
> = {
  EMAIL_INTAKE_NO_BILL_STATE_NO_BILL: {
    ptBr: 'O e-mail não gerou conta.',
    en: 'The e-mail did not create a bill.',
  },
  EMAIL_INTAKE_NO_BILL_STATE_RETRYING: {
    ptBr: 'Reprocessando: o e-mail foi enviado de novo para processamento.',
    en: 'Reprocessing: the e-mail was sent for processing again.',
  },
  EMAIL_INTAKE_NO_BILL_STATE_RETRY_NO_RESULT: {
    ptBr: 'Reenvio sem resultado: o novo processamento não terminou em 2 horas. Baixe os arquivos e lance a conta manualmente.',
    en: 'Retry without result: the new processing did not finish within 2 hours. Download the files and create the bill manually.',
  },
};

/** Where the e-mail came from: a person (sender address) or the Pipefy integration (Q16). */
export type EmailIntakeNoBillChannel = 'EMAIL' | 'PIPEFY';

export interface EmailIntakeNoBillItem {
  messageId: string;
  entityId: string;
  entityName: string | null;
  inboxAddress: string | null;
  channel: EmailIntakeNoBillChannel;
  /** Pipefy card id when `channel = PIPEFY` and the body names it. */
  pipefyCardId: string | null;
  fromAddress: string;
  fromName: string | null;
  subject: string | null;
  receivedAt: string;
  outcomeAt: string | null;
  /** Stable reason code (S1). Null only for rows written before the code list existed. */
  reasonCode: EmailIntakeReasonCode | null;
  /** Number of files the e-mail carried (attachments, plus Pipefy card files). */
  fileCount: number;
  files: EmailIntakeNoBillFile[];
  state: EmailIntakeNoBillRowState;
  /** Last "Tentar novamente" (server truth); null when the e-mail was never retried. */
  retryRequestedAt: string | null;
  /**
   * True while a run is queued or in flight (status RECEIVED / TRIAGING / READY_TO_PROCESS /
   * AWAITING_INFO, an unfinished attachment plan, or a retry younger than 2 h). The retry
   * endpoint refuses these rows with RETRY_IN_PROGRESS.
   */
  retryInProgress: boolean;
  /**
   * The companies a person may choose for this e-mail ("Escolher empresa"): the inbox's
   * entities the caller can access. Empty when the inbox serves one company or any company.
   */
  entityChoices: EmailIntakeNoBillEntityChoice[];
  /**
   * True when the caller must choose the company before "Tentar novamente", "Escolher
   * fornecedor" or "Anexar documento": the inbox serves companies outside their access and
   * nobody chose this e-mail's company yet. The API refuses those actions with
   * ENTITY_CHOICE_REQUIRED unless they send `entityId`.
   */
  entityChoiceRequired: boolean;
  /** The company a person chose on the last retry; null when nobody chose one. */
  chosenEntityId: string | null;
  /** The vendor a person chose on the last retry ("Escolher fornecedor"); null when none. */
  chosenVendorId: string | null;
  /**
   * A person may ask the sender for more information ("Pedir ao remetente"): the e-mail came
   * from a person (not Pipefy) and its sender can receive a reply.
   */
  canAskSender: boolean;
  /** Last time a person asked the sender from the card; null when nobody did. */
  senderAskedAt: string | null;
}

/** `GET /email-intake/no-bill/:messageId`: the row plus the e-mail text ("Ver e-mail"). */
export interface EmailIntakeNoBillDetail extends EmailIntakeNoBillItem {
  /**
   * The plain-text body, at most {@link EMAIL_INTAKE_NO_BILL_BODY_MAX_CHARS} characters. Null
   * when the e-mail has no text body. Never HTML.
   */
  bodyPlain: string | null;
  /** True when `bodyPlain` was cut at the limit. */
  bodyTruncated: boolean;
  /** The sender's last answer to a question about this e-mail; null when none was recorded. */
  senderReply: EmailIntakeSenderReply | null;
  /**
   * True when a file of the e-mail has a bill in a company outside the caller's access. The text
   * and the sender's answer can carry that company's amounts and bank details, so `bodyPlain` and
   * `senderReply` are null; the files keep their own rules.
   */
  bodyWithheld: boolean;
}

/**
 * The sender's reply that answered a question about an e-mail ("Pedir ao remetente", or "which
 * company?"). The worker stores it on the answered `PENDING_INFO` entry under
 * {@link EMAIL_INTAKE_SENDER_REPLY_KEY}; "Ver e-mail" shows it under the e-mail text.
 */
export interface EmailIntakeSenderReply {
  /** The reply e-mail's own id. */
  messageId: string;
  fromAddress: string;
  /** When the worker took the answer (ISO). */
  answeredAt: string;
  /** The new text of the reply, quoted history dropped, at most EMAIL_INTAKE_NO_BILL_BODY_MAX_CHARS. */
  text: string;
  /** True when `text` was cut at the limit. */
  textTruncated: boolean;
}

/** Key of {@link EmailIntakeSenderReply} on an answered `PENDING_INFO` history entry. */
export const EMAIL_INTAKE_SENDER_REPLY_KEY = 'senderReply';

/** The longest body `GET :messageId` returns; a longer one is cut. */
export const EMAIL_INTAKE_NO_BILL_BODY_MAX_CHARS = 20_000;

export interface EmailIntakeNoBillEntityChoice {
  entityId: string;
  entityName: string;
}

/**
 * Body of "Tentar novamente". `entityId` = "Escolher empresa": every bill goes to it.
 * `vendorId` = "Escolher fornecedor": every bill is put on that vendor, whatever the document says.
 */
export interface EmailIntakeNoBillRetryRequest {
  entityId?: string;
  vendorId?: string;
}

/**
 * "Anexar documento" accepts the files e-mail intake reads: PDF and images, at most 10 MB (the
 * worker's own attachment limits).
 */
export const EMAIL_INTAKE_NO_BILL_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
] as const;
export const EMAIL_INTAKE_NO_BILL_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

/** The longest message "Pedir ao remetente" sends. */
export const EMAIL_INTAKE_ASK_SENDER_MAX_CHARS = 4_000;

/**
 * `resumeContext` key of a question a person sent from the "Precisa de você" card ("Pedir ao
 * remetente"). The API sets it; the worker's reply handler reads it: the sender's files go back
 * onto the original e-mail, which is processed again, and no automatic e-mail is sent.
 */
export const EMAIL_INTAKE_ASKED_FROM_CARD_KEY = 'askedFromCard';

/** Body of "Pedir ao remetente": the text the person wrote. The recipient is always the sender. */
export interface EmailIntakeNoBillAskSenderRequest {
  message: string;
}

/** Result of "Pedir ao remetente": the e-mail now waits for the sender's answer. */
export interface EmailIntakeNoBillAskSenderResponse {
  messageId: string;
  /** The address the question went to: the e-mail's sender. */
  sentTo: string;
  /** ISO-8601 timestamp of the question. */
  askedAt: string;
  askedByUserId: string;
}

export interface EmailIntakeNoBillListResponse {
  items: EmailIntakeNoBillItem[];
  /** Rows shown among the newest candidate e-mails (the API reads at most 500, newest first). */
  total: number;
  page: number;
  limit: number;
}

export interface EmailIntakeNoBillDownloadUrlResponse {
  url: string;
  filename: string;
  expiresInMinutes: number;
}

export interface EmailIntakeNoBillRetryResponse {
  messageId: string;
  status: 'QUEUED';
}

/** Result of "Dispensar": the e-mail left the list. Who and when are the audit of that decision. */
export interface EmailIntakeNoBillDismissResponse {
  messageId: string;
  /** ISO-8601 timestamp of the dismissal. */
  dismissedAt: string;
  dismissedByUserId: string;
}

/** `details` of a 409 RETRY_ALREADY_BILLED / RETRY_PARTIALLY_BILLED body. */
export interface EmailIntakeNoBillBilledDetails {
  existingBillIds: string[];
  existingBills: EmailIntakeNoBillExistingBill[];
}

/** Stable refusal codes of the no-bill endpoints. Classify by code, never by message text. */
export const EMAIL_INTAKE_NO_BILL_ERROR_CODES = {
  /** The e-mail is not in the caller's no-bill list (other tenant, other entity, or not NO_BILL). */
  NOT_FOUND: 'EMAIL_INTAKE_NO_BILL_NOT_FOUND',
  /** The file id does not belong to this e-mail, or the file was never stored. */
  FILE_NOT_FOUND: 'EMAIL_INTAKE_NO_BILL_FILE_NOT_FOUND',
  /** A zip needs at least one stored file. */
  NO_FILES: 'EMAIL_INTAKE_NO_BILL_NO_FILES',
  /** A retry for this e-mail is already queued or running. */
  RETRY_IN_PROGRESS: 'EMAIL_INTAKE_RETRY_IN_PROGRESS',
  /** Every file of the e-mail already has a bill (L3): retrying would never make a new one. */
  RETRY_ALREADY_BILLED: 'EMAIL_INTAKE_RETRY_ALREADY_BILLED',
  /**
   * Some files already have a bill: a retry could bill a companion (boleto) of a billed NF on
   * its own. Send the remaining files manually instead.
   */
  RETRY_PARTIALLY_BILLED: 'EMAIL_INTAKE_NO_BILL_RETRY_PARTIALLY_BILLED',
  /**
   * A file of the e-mail is a document already routed to a company outside the caller's access
   * (its bill failed): running the e-mail again would move it, so only someone with access retries.
   */
  RETRY_DOCUMENT_OUTSIDE_SCOPE: 'EMAIL_INTAKE_NO_BILL_RETRY_DOCUMENT_OUTSIDE_SCOPE',
  /** The retry could not be queued (queue down); nothing changed, try again later. */
  RETRY_QUEUE_UNAVAILABLE: 'EMAIL_INTAKE_RETRY_QUEUE_UNAVAILABLE',
  /** `POST /webhooks/email/reprocess` named a tenant other than the caller's. */
  REPROCESS_TENANT_MISMATCH: 'EMAIL_INTAKE_REPROCESS_TENANT_MISMATCH',
  /**
   * Dismiss lost a race: the e-mail is still on the list, but not in the state the click saw
   * (for example a retry was claimed first).
   */
  CHANGED: 'EMAIL_INTAKE_NO_BILL_CHANGED',
  /** The chosen company is not one the inbox serves, or the caller cannot access it. */
  ENTITY_NOT_ALLOWED: 'EMAIL_INTAKE_NO_BILL_ENTITY_NOT_ALLOWED',
  /**
   * The caller may access only some of a shared inbox's companies and nobody chose this e-mail's
   * company yet: the run could book it in a company outside their access, so they choose first.
   */
  ENTITY_CHOICE_REQUIRED: 'EMAIL_INTAKE_NO_BILL_ENTITY_CHOICE_REQUIRED',
  /** The chosen vendor does not exist in the tenant, or is not active. */
  VENDOR_NOT_ALLOWED: 'EMAIL_INTAKE_NO_BILL_VENDOR_NOT_ALLOWED',
  /** "Anexar documento" got no file, an empty file, a file over 10 MB, or not a PDF, PNG or JPEG. */
  DOCUMENT_INVALID: 'EMAIL_INTAKE_NO_BILL_DOCUMENT_INVALID',
  /** The sender cannot get a reply: a Pipefy e-mail, or a sender with no real mailbox. */
  ASK_SENDER_NOT_ALLOWED: 'EMAIL_INTAKE_NO_BILL_ASK_SENDER_NOT_ALLOWED',
  /** The message to the sender is empty or longer than the limit. */
  ASK_SENDER_MESSAGE_INVALID: 'EMAIL_INTAKE_NO_BILL_ASK_SENDER_MESSAGE_INVALID',
  /** The e-mail could not be sent; nothing changed, try again later. */
  ASK_SENDER_SEND_FAILED: 'EMAIL_INTAKE_NO_BILL_ASK_SENDER_SEND_FAILED',
  /**
   * The e-mail provider did not answer in time, so the send may still happen. The question stays
   * pending; the provider's late answer settles it. Do not ask again.
   */
  ASK_SENDER_SEND_UNCONFIRMED: 'EMAIL_INTAKE_NO_BILL_ASK_SENDER_SEND_UNCONFIRMED',
} as const;

export type EmailIntakeNoBillErrorCode =
  (typeof EMAIL_INTAKE_NO_BILL_ERROR_CODES)[keyof typeof EMAIL_INTAKE_NO_BILL_ERROR_CODES];

export const EMAIL_INTAKE_NO_BILL_ERROR_CODE_VALUES = Object.values(
  EMAIL_INTAKE_NO_BILL_ERROR_CODES,
) as readonly EmailIntakeNoBillErrorCode[];

/**
 * Message per refusal code: pt-BR (users), en (web English locale), and an English `hint` for
 * agents / MCP callers saying what to do next. The web resolves its copy from the code.
 */
export const EMAIL_INTAKE_NO_BILL_ERROR_MESSAGES: Record<
  EmailIntakeNoBillErrorCode,
  StableRefusalCopy
> = {
  EMAIL_INTAKE_NO_BILL_NOT_FOUND: {
    ptBr: 'Este e-mail não está mais na lista de e-mails que não viraram conta.',
    en: 'This e-mail is no longer in the list of e-mails that did not become a bill.',
    hint: 'Reload the no-bill list; the e-mail may have become a bill or belong to an entity you cannot access.',
  },
  EMAIL_INTAKE_NO_BILL_FILE_NOT_FOUND: {
    ptBr: 'Este arquivo não está disponível para download.',
    en: 'This file is not available for download.',
    hint: 'Reload the row and use a fileId from its files list; files that were never stored cannot be downloaded.',
  },
  EMAIL_INTAKE_NO_BILL_NO_FILES: {
    ptBr: 'Este e-mail não tem arquivos guardados para baixar.',
    en: 'This e-mail has no stored files to download.',
    hint: 'Nothing to zip: ask the sender for the files or upload them manually.',
  },
  EMAIL_INTAKE_RETRY_IN_PROGRESS: {
    ptBr: 'Já estamos processando este e-mail de novo. Aguarde alguns minutos.',
    en: 'This e-mail is already being processed again. Wait a few minutes.',
    hint: 'Wait for the running retry to finish, then reload the list before retrying again.',
  },
  EMAIL_INTAKE_RETRY_ALREADY_BILLED: {
    ptBr: 'Todos os arquivos deste e-mail já têm conta. Não vamos lançar de novo.',
    en: 'Every file of this e-mail already has a bill. It will not be billed again.',
    hint: 'Open the existing bills listed in details.existingBills (billId + billNumber) instead of retrying.',
  },
  EMAIL_INTAKE_NO_BILL_RETRY_PARTIALLY_BILLED: {
    ptBr: 'Parte dos arquivos deste e-mail já tem conta. Envie os arquivos restantes manualmente.',
    en: 'Some files of this e-mail already have a bill. Send the remaining files manually.',
    hint: 'Do not retry: details.existingBills names the bills that exist; upload only the files whose existingBill is null, into the row entityId.',
  },
  EMAIL_INTAKE_NO_BILL_RETRY_DOCUMENT_OUTSIDE_SCOPE: {
    ptBr: 'Um arquivo deste e-mail já está em uma empresa que você não acessa. Peça para alguém dessa empresa tentar de novo.',
    en: 'A file of this e-mail is already in a company you cannot access. Ask someone from that company to try again.',
    hint: 'Do not retry: a file was routed to an entity outside your access. A user with access to that entity retries it.',
  },
  EMAIL_INTAKE_RETRY_QUEUE_UNAVAILABLE: {
    ptBr: 'Não conseguimos colocar este e-mail na fila agora. Tente de novo em alguns minutos.',
    en: 'We could not queue this e-mail right now. Try again in a few minutes.',
    hint: 'The processing queue is unavailable; nothing changed. Retry later.',
  },
  EMAIL_INTAKE_REPROCESS_TENANT_MISMATCH: {
    ptBr: 'Você só pode reprocessar e-mails da sua própria empresa.',
    en: 'You can only reprocess e-mails of your own company.',
    hint: 'Send the tenantId of the authenticated session; other tenants are never reprocessed.',
  },
  EMAIL_INTAKE_NO_BILL_CHANGED: {
    ptBr: 'Este e-mail mudou enquanto você o dispensava. Atualize a lista e tente de novo.',
    en: 'This e-mail changed while you were dismissing it. Refresh the list and try again.',
    hint: 'Reload the no-bill list and dismiss again if the e-mail is still shown.',
  },
  EMAIL_INTAKE_NO_BILL_ENTITY_NOT_ALLOWED: {
    ptBr: 'Esta empresa não pode receber contas desta caixa de e-mail.',
    en: 'This company cannot receive bills from this inbox.',
    hint: 'Choose an entityId from the row entityChoices.',
  },
  EMAIL_INTAKE_NO_BILL_ENTITY_CHOICE_REQUIRED: {
    ptBr: 'Escolha primeiro a empresa deste e-mail.',
    en: 'Choose the company of this e-mail first.',
    hint: 'Send entityId (one of the row entityChoices): the row has entityChoiceRequired = true.',
  },
  EMAIL_INTAKE_NO_BILL_VENDOR_NOT_ALLOWED: {
    ptBr: 'Este fornecedor não está ativo. Escolha outro fornecedor.',
    en: 'This vendor is not active. Choose another vendor.',
    hint: 'Pass the vendorId of an ACTIVE vendor of the tenant (GET ap/vendors).',
  },
  EMAIL_INTAKE_NO_BILL_DOCUMENT_INVALID: {
    ptBr: 'Envie um arquivo PDF, PNG ou JPEG de até 10 MB.',
    en: 'Send a PDF, PNG or JPEG file of at most 10 MB.',
    hint: 'Send one non-empty file in the multipart field "file" (PDF, PNG or JPEG, at most 10 MB).',
  },
  EMAIL_INTAKE_NO_BILL_ASK_SENDER_NOT_ALLOWED: {
    ptBr: 'Não é possível responder a este remetente.',
    en: 'This sender cannot get a reply.',
    hint: 'Only e-mails with canAskSender = true accept a question; Pipefy e-mails do not.',
  },
  EMAIL_INTAKE_NO_BILL_ASK_SENDER_MESSAGE_INVALID: {
    ptBr: 'Escreva a mensagem, com até 4.000 caracteres.',
    en: 'Write the message, with at most 4,000 characters.',
    hint: 'Send a non-empty message of at most EMAIL_INTAKE_ASK_SENDER_MAX_CHARS characters.',
  },
  EMAIL_INTAKE_NO_BILL_ASK_SENDER_SEND_FAILED: {
    ptBr: 'Não conseguimos enviar a mensagem agora. Tente de novo em alguns minutos.',
    en: 'We could not send the message right now. Try again in a few minutes.',
    hint: 'The e-mail provider refused the send; nothing changed. Retry later.',
  },
  EMAIL_INTAKE_NO_BILL_ASK_SENDER_SEND_UNCONFIRMED: {
    ptBr: 'O envio está demorando para ser confirmado. Não envie de novo: atualize a página em alguns minutos.',
    en: 'The send is taking long to confirm. Do not send again: refresh the page in a few minutes.',
    hint: 'The e-mail provider did not answer in time; the question stays pending and is settled when the provider answers. Do not ask again.',
  },
};

export function isEmailIntakeNoBillErrorCode(value: unknown): value is EmailIntakeNoBillErrorCode {
  return (
    typeof value === 'string' &&
    (EMAIL_INTAKE_NO_BILL_ERROR_CODE_VALUES as readonly string[]).includes(value)
  );
}

/**
 * The BullMQ job id of one S5 retry: the e-mail plus its retry stamp. A late re-send of the same
 * enqueue (BullMQ's offline queue after a Redis blip) lands on the same job, so triage runs once;
 * a later retry has a new stamp and a new job.
 */
export function emailIntakeRetryJobId(messageId: string, retryRequestedAt: Date): string {
  return `email-intake-retry-${messageId}-${retryRequestedAt.getTime()}`;
}
